import { JobStatus, JobTypes } from "@lib/enums";
import { useCallback, useContext, useState } from "react";
import useUpdateJobStatuses from "./useUpdateJobStatuses";
import { JobStatusContext } from "./JobStatusContext";

export const initialJob = {
  id: null,
  status: JobStatus.PENDING,
  type: JobTypes.AI,
  metadata: {},
  error: null,
};

const pendingStatuses = [JobStatus.PENDING, JobStatus.QUEUED];

export function useInitialiseJobStatus() {
  const [jobs, setJobs] = useState([]);
  const { jobs: fetchedJobs } = useUpdateJobStatuses();

  // Merge server-fetched jobs with any locally-added pending jobs (those not yet
  // uploaded to the server) whenever the fetched set changes. Done during render
  // (React's replacement for a set-state-in-effect); keyed on a stable hash of
  // fetchedJobs so it only re-merges when the server data actually changes.
  const [prevFetchedHash, setPrevFetchedHash] = useState(null);
  const fetchedHash = JSON.stringify(fetchedJobs);
  if (fetchedHash !== prevFetchedHash) {
    setPrevFetchedHash(fetchedHash);
    setJobs((jobs) => {
      const fetchedFileIds = fetchedJobs.map(
        (job) => job.metadata?.fileId ?? null
      );

      // make sure we include any files that haven't been uploaded to the server yet
      // pending means not yet uploaded
      const pending = jobs.filter(
        (job) =>
          pendingStatuses.includes(job.status) &&
          !fetchedFileIds.includes(job.id ?? null)
      );

      return [...pending, ...fetchedJobs];
    });
  }

  const addJob = useCallback((id, options) => {
    const newId = id || Date.now();
    setJobs((jobs) => [
      ...jobs,
      {
        ...initialJob,
        ...options,
        id: newId,
      },
    ]);
  }, []);

  const updateJob = useCallback((id, updates) => {
    setJobs((jobs) => [
      ...jobs.filter((job) => job.id !== id),
      {
        ...jobs.find((job) => job.id === id),
        ...updates,
      },
    ]);
  }, []);

  const removeJobs = useCallback((ids) => {
    // Only access localStorage on the client side
    if (typeof window === "undefined") return;

    // Get existing removed job IDs from localStorage or initialize as empty array
    const existingRemovedJobs = JSON.parse(
      localStorage.getItem("removedJobIds") || "[]"
    );

    // Combine existing and new IDs, then remove duplicates
    const updatedRemovedJobs = [...new Set([...existingRemovedJobs, ...ids])];

    // Save back to localStorage
    localStorage.setItem("removedJobIds", JSON.stringify(updatedRemovedJobs));

    // Update the state to filter out the removed jobs
    setJobs((jobs) =>
      jobs.filter((job) => !updatedRemovedJobs.includes(job.id))
    );
  }, []);

  return { jobs, addJob, updateJob, removeJobs };
}

export function useJobStatus(type = "", status = []) {
  const { jobs, addJob, updateJob, removeJobs } = useContext(JobStatusContext);

  // Get the list of removed job IDs from localStorage (client-side only)
  const removedJobIds =
    typeof window !== "undefined"
      ? JSON.parse(localStorage.getItem("removedJobIds") || "[]")
      : [];

  const filtered = jobs
    .filter((job) => !Boolean(type) || job.type === type)
    .filter((job) => !status.length || status.includes(job.status))
    .filter((job) => !removedJobIds.includes(job.id));

  return { jobs: filtered, addJob, updateJob, removeJobs };
}
